import React, { useMemo } from 'react';
import TableComponent from '../TableComponent';
import { renderTableCell } from '../../utils/commonfunctions';
import { FEEDBACK_STATUSES } from '../../enums/FeedbackEnumFrontend';
import {
  getFeedbackTypeLabel,
  getFeedbackStatusLabel,
} from '../../utils/feedbackUtils';
import ChipComponent from '../chips/ChipComponent/ChipComponent';
import ChangeStatus from '../chips/ChangeStatus';
import { getFeedbackTypeVariant, getFeedbackStatusVariant } from '../chips/ChipComponent/chipVariants';
import styles from '../../styles/feedback/FeedbackTable.module.scss';

const TABLE_CONTENT = {
  columns: {
    type: 'Type',
    description: 'Description',
    submittedBy: 'Submitted By',
    organisation: 'Organisation',
    page: 'Page',
    submittedAt: 'Submitted At',
    status: 'Status',
  },
  statusOptions: [
    FEEDBACK_STATUSES.NEW,
    FEEDBACK_STATUSES.REVIEWED,
    FEEDBACK_STATUSES.ACTIONED,
  ],
  emptyState: 'No feedback submissions found.',
  orgFallback: '—',
};

const TABLE_TITLE = 'User Feedback';
const selectedRows = [];
const setSelectedRows = () => { };
const handleRowClick = () => { };

const buildColumns = (onStatusUpdate) => [
  {
    field: 'type',
    headerName: TABLE_CONTENT.columns.type,
    flex: 0.9,
    minWidth: 140,
    disableColumnMenu: true,
    sortable: false,
    renderCell: (params) => (
      <ChipComponent
        variant={getFeedbackTypeVariant(params.row.type)}
        size="small"
      >
        {getFeedbackTypeLabel(params.row.type)}
      </ChipComponent>
    ),
  },
  {
    field: 'description',
    headerName: TABLE_CONTENT.columns.description,
    flex: 2,
    minWidth: 200,
    disableColumnMenu: true,
    sortable: false,
    renderCell: (params) => renderTableCell(params.row.description),
  },
  {
    field: 'submittedBy',
    headerName: TABLE_CONTENT.columns.submittedBy,
    flex: 1.2,
    minWidth: 160,
    disableColumnMenu: true,
    sortable: false,
    renderCell: (params) => {
      const name = params.row.user?.username || TABLE_CONTENT.orgFallback;
      const email = params.row.user?.email || '';
      return (
        <div className={styles.submittedByCell}>
          <span className={styles.submittedByName}>{name}</span>
          {email && <span className={styles.submittedByEmail}>{email}</span>}
        </div>
      );
    },
  },
  {
    field: 'organisation',
    headerName: TABLE_CONTENT.columns.organisation,
    flex: 1,
    minWidth: 130,
    disableColumnMenu: true,
    sortable: false,
    renderCell: (params) => params.row.organization?.name || TABLE_CONTENT.orgFallback,
  },
  {
    field: 'current_route',
    headerName: TABLE_CONTENT.columns.page,
    flex: 1,
    minWidth: 120,
    disableColumnMenu: true,
    sortable: false,
    renderCell: (params) => renderTableCell(params.row.current_route),
  },
  {
    field: 'created_at',
    headerName: TABLE_CONTENT.columns.submittedAt,
    flex: 1,
    minWidth: 140,
    disableColumnMenu: true,
    sortable: false,
    renderCell: (params) => {
      if (!params.row.created_at) return TABLE_CONTENT.orgFallback;
      return new Date(params.row.created_at).toLocaleDateString('en-IN', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      });
    },
  },
  {
    field: 'status',
    headerName: TABLE_CONTENT.columns.status,
    flex: 1,
    minWidth: 150,
    disableColumnMenu: true,
    sortable: false,
    renderCell: (params) => (
      <ChangeStatus
        status={params.row.status || FEEDBACK_STATUSES.NEW}
        availableStatuses={TABLE_CONTENT.statusOptions}
        getVariant={getFeedbackStatusVariant}
        formatStatus={getFeedbackStatusLabel}
        onStatusChange={(newStatus) => {
          onStatusUpdate(params.row.id, newStatus, params.row.status);
        }}
        confirmChange={false}
        size="small"
        showTooltip={false}
      />
    ),
  },
];

const FeedbackTable = ({
  data,
  total,
  loading,
  paginationModel,
  onPaginationChange,
  onStatusUpdate,
}) => {
  const rows = loading || !data ? [] : data.filter((row) => row != null && typeof row === 'object');
  const columns = useMemo(() => buildColumns(onStatusUpdate), [onStatusUpdate]);

  return (
    <TableComponent
      viewType="FeedbackList"
      title={TABLE_TITLE}
      hideColumnsVisibilityButton
      rows={rows}
      columns={columns}
      loading={loading}
      idField="id"
      isColumnResizable={!loading}
      isRowSelection={false}
      handleRowSelection={setSelectedRows}
      selectedRows={selectedRows}
      handleRowClick={handleRowClick}
      isPagination
      paginationMode="server"
      rowCount={total || 0}
      paginationModel={paginationModel}
      handlePaginationModelChange={onPaginationChange}
      rowsPerPageOptions={[25, 50, 100]}
    />
  );
};

export default FeedbackTable;
